import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Card, CardContent, Grid,
  LinearProgress, Alert, Avatar, Tooltip, Divider,
} from '@mui/material';
import { AccountTree, Star, Warning } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string) {
  return fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const READINESS_CFG: Record<string, { color: string; bg: string; label: string; short: string }> = {
  ready_now:     { color: '#2e7d32', bg: '#e8f5e9', label: 'Ready Now',    short: 'Now' },
  ready_1_year:  { color: '#e65100', bg: '#fff3e0', label: 'Ready 1 Yr',   short: '1yr' },
  ready_2_years: { color: '#1565c0', bg: '#e3f2fd', label: 'Ready 2 Yrs',  short: '2yr' },
  long_term:     { color: '#6a1b9a', bg: '#f3e5f5', label: 'Long Term',    short: 'LT' },
  not_ready:     { color: '#9e9e9e', bg: '#f5f5f5', label: 'Not Ready',    short: '—' },
};

const RISK_CFG: Record<string, { color: any; bg: string; label: string }> = {
  low:      { color: 'success', bg: '#e8f5e9', label: '🟢 Low Risk' },
  medium:   { color: 'warning', bg: '#fff3e0', label: '🟡 Medium Risk' },
  high:     { color: 'error',   bg: '#ffebee', label: '🔴 High Risk' },
  critical: { color: 'error',   bg: '#fce4ec', label: '🚨 Critical' },
};

function initials(first?: string, last?: string) { return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase(); }

interface Successor { firstName: string; lastName: string; department: string; jobTitle: string; readiness: string; rank: number; gaps?: string; retentionRisk?: string; }
interface PipelineItem { id: string; positionTitle: string; isCritical: boolean; incFirstName?: string; incLastName?: string; incTitle?: string; department?: string; successors: Successor[]; coverageRisk: string; readyNow: number; ready1Yr: number; successorCount: number; }
interface Stats { totalPositions: number; criticalPositions: number; uncovered: number; readyNowTotal: number; coverageByRisk: Record<string, number>; }

export default function SuccessionPipeline() {
  const [pipeline, setPipeline] = useState<PipelineItem[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [riskFilter, setRiskFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    apiFetch('/api/succession-pipeline')
      .then(d => { setPipeline(d.pipeline || []); setStats(d.stats); setLoading(false); })
      .catch((e: any) => { setError(e.message); setLoading(false); });
  }, []);

  const displayed = riskFilter ? pipeline.filter(p => p.coverageRisk === riskFilter) : pipeline;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AccountTree sx={{ fontSize: 32, color: '#4a148c' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Succession Pipeline</Typography>
          <Typography variant="body2" color="text.secondary">Coverage and readiness for key positions</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Key Positions',    value: stats.totalPositions,     color: '#4a148c' },
            { label: 'Critical',         value: stats.criticalPositions,  color: '#c62828' },
            { label: 'No Successor',     value: stats.uncovered,          color: '#e65100' },
            { label: 'Ready Now',        value: stats.readyNowTotal,      color: '#2e7d32' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card sx={{ textAlign: 'center', p: 1 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {(stats?.uncovered ?? 0) > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#fce4ec', border: '1px solid #e57373', display: 'flex', gap: 1, alignItems: 'center' }}>
          <Warning sx={{ color: '#c62828' }} />
          <Typography variant="body2" color="error.dark" fontWeight={600}>
            {stats?.uncovered} critical position{(stats?.uncovered ?? 0) > 1 ? 's' : ''} without a named successor
          </Typography>
        </Paper>
      )}

      {/* Risk filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="All" size="small" onClick={() => setRiskFilter('')}
          color={!riskFilter ? 'primary' : 'default'} variant={!riskFilter ? 'filled' : 'outlined'} />
        {Object.entries(RISK_CFG).map(([k, v]) => (
          <Chip key={k} label={`${v.label} (${stats?.coverageByRisk[k] ?? 0})`} size="small"
            onClick={() => setRiskFilter(k === riskFilter ? '' : k)}
            color={riskFilter === k ? v.color : 'default'} variant={riskFilter === k ? 'filled' : 'outlined'} />
        ))}
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        {displayed.map(p => {
          const risk = RISK_CFG[p.coverageRisk];
          return (
            <Grid key={p.id} size={{ xs: 12, md: 6, lg: 4 }}>
              <Paper sx={{ border: `2px solid ${risk.bg}`, overflow: 'hidden' }}>
                {/* Header */}
                <Box sx={{ bgcolor: risk.bg, px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {p.isCritical && <Star sx={{ fontSize: 16, color: '#f57f17' }} />}
                      <Typography fontWeight={700}>{p.positionTitle}</Typography>
                    </Box>
                    {p.incFirstName && (
                      <Typography variant="caption" color="text.secondary">
                        Incumbent: {p.incFirstName} {p.incLastName} · {p.department}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={risk.label} size="small" color={risk.color} />
                </Box>

                {/* Readiness bar */}
                <Box sx={{ px: 2, py: 1 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                    {p.readyNow > 0 && <Chip label={`${p.readyNow} Ready Now`} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.65rem' }} />}
                    {p.ready1Yr > 0 && <Chip label={`${p.ready1Yr} in 1yr`} size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.65rem' }} />}
                    {p.successorCount === 0 && <Chip label="No successors" size="small" color="error" sx={{ fontSize: '0.65rem' }} />}
                  </Box>

                  {p.successors.length > 0 && <Divider sx={{ mb: 1 }} />}

                  {/* Successor cards */}
                  {p.successors.map((s, i) => {
                    const rc = READINESS_CFG[s.readiness] ?? READINESS_CFG.not_ready;
                    return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#f9f9f9' } }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: '#4a148c', fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}>
                          {initials(s.firstName, s.lastName)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{s.firstName} {s.lastName}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>{s.jobTitle} · {s.department}</Typography>
                        </Box>
                        <Tooltip title={s.gaps ? `Gaps: ${s.gaps}` : rc.label}>
                          <Chip label={rc.short} size="small" sx={{ bgcolor: rc.bg, color: rc.color, fontWeight: 700, fontSize: '0.6rem', minWidth: 32 }} />
                        </Tooltip>
                        {s.retentionRisk === 'high' && <Tooltip title="High retention risk"><Warning sx={{ fontSize: 14, color: '#e65100' }} /></Tooltip>}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          );
        })}
        {displayed.length === 0 && !loading && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <AccountTree sx={{ fontSize: 48, opacity: 0.3 }} />
              <Typography>No succession plans found. Create them in Succession Planning.</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
